// Modal Nova/Editar campanha no padrão Aeros (mockup "Campanhas Modais e
// Relatório"): header com segmented WhatsApp/SMS, stepper informativo,
// coluna esquerda de configuração (lista/tag, conexão, agendamento,
// recorrência, ticket) e coluna direita de mensagens (abas 1–5, variáveis,
// anexo, confirmações, pré-visualização). A lógica de salvar, SMS,
// templates oficiais e custos é a mesma de antes — só a apresentação mudou.
import React, { useState, useEffect, useRef, useContext } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { head } from "lodash";
import { makeStyles, alpha } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import CircularProgress from "@material-ui/core/CircularProgress";
import Switch from "@material-ui/core/Switch";
import Select from "@material-ui/core/Select";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import Popover from "@material-ui/core/Popover";
import CloseIcon from "@material-ui/icons/Close";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import SmsOutlinedIcon from "@material-ui/icons/SmsOutlined";
import CheckIcon from "@material-ui/icons/Check";
import RepeatIcon from "@material-ui/icons/Repeat";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import ImageOutlinedIcon from "@material-ui/icons/ImageOutlined";
import InsertDriveFileOutlinedIcon from "@material-ui/icons/InsertDriveFileOutlined";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import MoodIcon from "@material-ui/icons/Mood";
import FormatBoldIcon from "@material-ui/icons/FormatBold";
import FormatItalicIcon from "@material-ui/icons/FormatItalic";
import ScheduleIcon from "@material-ui/icons/Schedule";
import SendIcon from "@material-ui/icons/Send";
import RocketIcon from "@material-ui/icons/FlightTakeoff";
import ListAltIcon from "@material-ui/icons/ListAlt";
import LocalOfferOutlinedIcon from "@material-ui/icons/LocalOfferOutlined";
import EventIcon from "@material-ui/icons/Event";
import { Picker } from "emoji-mart";
import { i18n } from "../../translate/i18n";
import moment from "moment";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import ConfirmationModal from "../ConfirmationModal";
import ComprarCreditosSmsModal from "../ComprarCreditosSmsModal";
import Autocomplete, { createFilterOptions } from "@material-ui/lab/Autocomplete";
import useQueues from "../../hooks/useQueues";
import TemplateFields, { deveLimparTemplateNaTrocaDeConexao } from "./TemplateFields";
import useAerosModalStyles from "../../styles/aerosModalStyles";
import useAerosSettingsStyles from "../Settings/aerosSettingsStyles";
import { AEROS, stateText } from "../../styles/aerosTokens";
import { estimateLabel, limiteDiarioDoCanal, nextExecutions, nextExecutionsLabel, RECURRENCE_TYPES, WEEKDAY_SHORT, previewMessage, filledMessages, erroUrlMidiaCabecalho, opcoesRotaSms } from "./campaignRules";

const tc = (k, o) => i18n.t(`aeros.campaign.${k}`, o);

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const line = isLight ? theme.palette.divider : "rgba(255,255,255,0.07)";
  const border = isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.1)";
  const purple = stateText(theme, "#C08BFF", "purple");
  const cyan = stateText(theme, AEROS.cyan, "info");
  return {
    root: {},
    paper: { width: 960, maxWidth: "calc(100vw - 32px)", margin: 16 },
    header: { padding: "20px 24px 0", alignItems: "flex-start" },
    segmented: { marginLeft: "auto", display: "flex", gap: 2, backgroundColor: theme.palette.background.paper, border: `1px solid ${line}`, borderRadius: 10, padding: 3, fontSize: 12, fontWeight: 600 },
    seg: { padding: "6px 14px", borderRadius: 8, color: theme.palette.text.secondary, display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", font: "inherit", "& .MuiSvgIcon-root": { fontSize: 14 } },
    segOn: { backgroundColor: alpha("#A044FF", 0.2), color: purple },
    steps: { display: "flex", padding: "16px 24px 0", borderBottom: `1px solid ${line}` },
    step: { display: "flex", alignItems: "center", gap: 8, padding: "8px 16px 12px 0", marginRight: 16, borderBottom: "2px solid transparent", color: theme.palette.aeros.textTertiary, fontSize: 12.5, fontWeight: 600 },
    stepOn: { color: theme.palette.text.primary, borderBottomColor: AEROS.cyan },
    stepDone: { color: theme.palette.text.primary },
    dot: { width: 20, height: 20, borderRadius: "50%", backgroundColor: isLight ? theme.palette.action.hover : "rgba(255,255,255,0.06)", color: theme.palette.aeros.textTertiary, fontSize: 10.5, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", "& .MuiSvgIcon-root": { fontSize: 13 } },
    dotOn: { background: "linear-gradient(135deg,#A044FF,#22D3EE)", color: "#07080D" },
    dotDone: { backgroundColor: alpha(AEROS.success, 0.15), color: stateText(theme, AEROS.success, "success") },
    grid: { display: "grid", gridTemplateColumns: "1fr 1fr", [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" } },
    left: { padding: "20px 24px", borderRight: `1px solid ${line}`, display: "flex", flexDirection: "column", gap: 14, minWidth: 0 },
    right: { padding: "20px 24px", display: "flex", flexDirection: "column", gap: 12, backgroundColor: isLight ? theme.palette.action.hover : "#151926", minWidth: 0 },
    label: { fontSize: 12, fontWeight: 600, color: theme.palette.text.secondary, marginBottom: 6, display: "block" },
    row2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
    row3: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 },
    field: { backgroundColor: theme.palette.aeros.surface, border: `1px solid ${border}`, borderRadius: 10, padding: "9px 12px", fontSize: 13, display: "flex", alignItems: "center", gap: 6, minHeight: 40, boxSizing: "border-box", "& input, & select, & textarea": { flex: 1, minWidth: 0, background: "none", border: "none", outline: "none", font: "inherit", color: theme.palette.text.primary, colorScheme: theme.palette.type, "&::placeholder": { color: theme.palette.aeros.textTertiary } }, "& .MuiSvgIcon-root": { fontSize: 16, color: theme.palette.text.secondary }, "&:focus-within": { borderColor: AEROS.cyan, boxShadow: "0 0 0 3px rgba(34,211,238,0.15)" } },
    fieldError: { borderColor: `${AEROS.error} !important` },
    errorText: { fontSize: 11, color: stateText(theme, AEROS.error, "error"), marginTop: 4 },
    count: { fontSize: 11, color: theme.palette.aeros.textTertiary },
    sel: { fontSize: 13, color: theme.palette.text.primary, flex: 1, "& .MuiNativeSelect-select": { padding: 0, paddingRight: 20, background: "transparent", "&:focus": { background: "transparent" } }, "& .MuiNativeSelect-icon": { fontSize: 16, color: theme.palette.aeros.textTertiary, top: "calc(50% - 8px)" } },
    conn: { width: 7, height: 7, borderRadius: "50%", flex: "none" },
    card: { backgroundColor: theme.palette.aeros.surface, border: `1px solid ${line}`, borderRadius: 12, padding: "12px 14px", display: "flex", flexDirection: "column", gap: 12 },
    cardHead: { display: "flex", alignItems: "center", gap: 10, "& .MuiSvgIcon-root": { fontSize: 17, color: purple } },
    cardTitle: { fontSize: 12.5, fontWeight: 600, color: theme.palette.text.primary },
    cardSub: { fontSize: 11, color: theme.palette.text.secondary },
    recGrid: { display: "grid", gridTemplateColumns: "1.2fr 0.8fr 1fr 1fr", gap: 8 },
    small: { fontSize: 10.5, fontWeight: 600, color: theme.palette.aeros.textTertiary, marginBottom: 4, display: "block" },
    fieldSm: { padding: "7px 10px", fontSize: 12, minHeight: 34, borderRadius: 8, backgroundColor: theme.palette.background.paper },
    days: { display: "flex", gap: 4 },
    day: { flex: 1, textAlign: "center", fontSize: 11, fontWeight: 700, borderRadius: 7, padding: "6px 0", color: theme.palette.aeros.textTertiary, background: "none", border: `1px solid ${line}`, cursor: "pointer", font: "inherit" },
    dayOn: { color: purple, backgroundColor: alpha("#A044FF", 0.15), borderColor: "rgba(160,68,255,0.4)" },
    info: { fontSize: 11, color: theme.palette.text.secondary, display: "flex", alignItems: "center", gap: 6, "& .MuiSvgIcon-root": { fontSize: 14, color: cyan } },
    msgHead: { display: "flex", alignItems: "center", gap: 8 },
    msgTabs: { marginLeft: "auto", display: "flex", gap: 4 },
    tab: { width: 28, height: 26, borderRadius: 7, fontSize: 11.5, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", color: theme.palette.aeros.textTertiary, background: "none", border: `1px solid ${line}`, cursor: "pointer", font: "inherit" },
    tabFilled: { color: theme.palette.text.primary, borderColor: isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.2)" },
    tabOn: { color: purple, backgroundColor: alpha("#A044FF", 0.15), borderColor: "transparent" },
    editor: { backgroundColor: theme.palette.aeros.surface, border: `1px solid ${border}`, borderRadius: 12, overflow: "hidden", "&:focus-within": { borderColor: AEROS.cyan, boxShadow: "0 0 0 3px rgba(34,211,238,0.15)" } },
    textarea: { display: "block", width: "100%", boxSizing: "border-box", padding: "12px 14px", fontSize: 13, lineHeight: 1.55, minHeight: 120, background: "none", border: "none", outline: "none", font: "inherit", color: theme.palette.text.primary, resize: "vertical", "&::placeholder": { color: theme.palette.aeros.textTertiary }, "&:disabled": { opacity: 0.6 } },
    editorBar: { display: "flex", alignItems: "center", gap: 6, padding: "8px 12px", borderTop: `1px solid ${line}`, backgroundColor: isLight ? theme.palette.action.hover : "rgba(255,255,255,0.02)", flexWrap: "wrap" },
    varChip: { fontSize: 11, fontWeight: 600, color: cyan, backgroundColor: alpha(AEROS.cyan, 0.1), borderRadius: 6, padding: "3px 8px", fontFamily: "ui-monospace, monospace", border: "none", cursor: "pointer" },
    barIcons: { marginLeft: "auto", display: "flex", gap: 2, "& .MuiIconButton-root": { padding: 3, color: theme.palette.text.secondary }, "& .MuiSvgIcon-root": { fontSize: 17 } },
    attach: { display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", backgroundColor: theme.palette.aeros.surface, border: `1px dashed ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.15)"}`, borderRadius: 10 },
    attachBtn: { cursor: "pointer", color: theme.palette.text.secondary, fontSize: 12.5, fontWeight: 600, background: "none", border: "none", font: "inherit", display: "flex", alignItems: "center", gap: 8, width: "100%", textAlign: "left", "& .MuiSvgIcon-root": { fontSize: 18 } },
    thumb: { width: 40, height: 40, borderRadius: 8, background: "linear-gradient(135deg,#1B1438,#0B2A33)", display: "flex", alignItems: "center", justifyContent: "center", flex: "none", "& .MuiSvgIcon-root": { fontSize: 20, color: "#C08BFF" } },
    preview: { marginTop: "auto", backgroundColor: theme.palette.background.paper, border: `1px solid ${line}`, borderRadius: 12, padding: 12 },
    previewLabel: { fontSize: 10.5, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: theme.palette.aeros.textTertiary, marginBottom: 8 },
    bubble: { maxWidth: "88%", marginLeft: "auto", backgroundColor: alpha("#A044FF", isLight ? 0.08 : 0.16), border: "1px solid rgba(160,68,255,0.3)", borderRadius: "12px 12px 4px 12px", overflow: "hidden", fontSize: 12, lineHeight: 1.5, color: theme.palette.text.primary, whiteSpace: "pre-wrap", wordBreak: "break-word" },
    bubbleImg: { height: 64, background: "linear-gradient(135deg,#1B1438,#0B2A33)" },
    bubbleText: { padding: "8px 10px", "& small": { fontSize: 10, color: theme.palette.text.secondary, marginLeft: 6 } },
    footer: { padding: "14px 24px", borderTop: `1px solid ${line}`, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" },
    estimate: { fontSize: 12, color: theme.palette.text.secondary, display: "flex", alignItems: "center", gap: 6, "& .MuiSvgIcon-root": { fontSize: 15, color: AEROS.warning } },
    footBtns: { marginLeft: "auto", display: "flex", gap: 10 },
  };
});

const MESSAGE_VARS = [
  { token: "{nome}", key: "nome" },
  { token: "{primeiro_nome}", key: "primeiro_nome" },
  { token: "{saudacao}", key: "saudacao" },
  { token: "{numero}", key: "numero" },
  { token: "{email}", key: "email" },
];

// No CampaignModal.js - Atualizar o schema de validação

const CampaignSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .required("Required"),
  isRecurring: Yup.boolean().default(false),
  recurrenceType: Yup.string().when('isRecurring', {
    is: true,
    then: Yup.string().oneOf(['minutely', 'hourly', 'daily', 'weekly', 'biweekly', 'monthly', 'yearly']).required('Tipo de recorrência é obrigatório'),
    otherwise: Yup.string().nullable()
  }),
  recurrenceInterval: Yup.number().when('isRecurring', {
    is: true,
    then: Yup.number().min(1, 'Intervalo deve ser maior que 0').required('Intervalo é obrigatório'),
    otherwise: Yup.number().nullable()
  }),
  // CORREÇÃO: Validar como array mas permitir vazio
  recurrenceDaysOfWeek: Yup.array().when(['isRecurring', 'recurrenceType'], {
    is: (isRecurring, recurrenceType) => isRecurring && recurrenceType === 'weekly',
    then: Yup.array().min(1, 'Selecione pelo menos um dia da semana').required(),
    otherwise: Yup.array().nullable()
  }),
  recurrenceDayOfMonth: Yup.number().when(['isRecurring', 'recurrenceType'], {
    is: (isRecurring, recurrenceType) => isRecurring && recurrenceType === 'monthly',
    then: Yup.number().min(1, 'Dia deve ser entre 1 e 31').max(31, 'Dia deve ser entre 1 e 31').required('Dia do mês é obrigatório'),
    otherwise: Yup.number().nullable()
  }),
  recurrenceEndDate: Yup.date().when('isRecurring', {
    is: true,
    then: Yup.date().min(new Date(), 'Data final deve ser futura').nullable(),
    otherwise: Yup.date().nullable()
  }),
  maxExecutions: Yup.number().when('isRecurring', {
    is: true,
    then: Yup.number().min(1, 'Número máximo deve ser maior que 0').nullable(),
    otherwise: Yup.number().nullable()
  })
});

// Duplicata em JS da regra de fracionamento do backend (é exibição, não
// cobrança — a contagem oficial é sempre feita no servidor no envio).
const calcularFracoesSms = (texto) => {
  const len = (texto || "").length;
  if (len === 0) return 0;
  return len <= 160 ? 1 : Math.ceil(len / 153);
};

// Mesma sentinela de 35 chars do backend (Fracoes.ts): contar o {link} cru
// (6 chars) mostraria "1 SMS" para mensagens que cobram 2 no disparo.
const LINK_SENTINELA_SMS = "https://xxxxxxxxx.com.br/s/xAbc1234";
const expandeLinkParaContagem = (texto) =>
  (texto || "").replace(/{link}/g, LINK_SENTINELA_SMS);

// Painel de custo da campanha SMS: mantém seu próprio debounce/estado para
// não acoplar o fetch de estimativa ao render principal do Formik.
const SmsCostPanel = ({ contactListId, message1, rota }) => {
  const [saldo, setSaldo] = useState(null);
  const [saldoError, setSaldoError] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [estimateError, setEstimateError] = useState(false);
  const [comprarCreditosOpen, setComprarCreditosOpen] = useState(false);

  useEffect(() => {
    api
      .get("/sms/saldo")
      .then(({ data }) => {
        setSaldo(data?.saldo ?? null);
        setSaldoError(false);
      })
      .catch(() => {
        setSaldo(null);
        setSaldoError(true);
      });
  }, []);

  useEffect(() => {
    if (!contactListId) {
      setEstimate(null);
      setEstimateError(false);
      return;
    }
    const delayDebounceFn = setTimeout(() => {
      api
        .get("/campaigns/sms-estimate", {
          params: { contactListId, message: message1 || "", rota: rota || undefined },
        })
        .then(({ data }) => {
          setEstimate(data);
          setEstimateError(false);
        })
        .catch(() => {
          setEstimate(null);
          setEstimateError(true);
        });
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [contactListId, message1, rota]);

  if (!contactListId) {
    return (
      <Typography variant="body2" color="textSecondary">
        Selecione uma lista de contatos para ver a estimativa de custo.
      </Typography>
    );
  }

  if (estimateError) {
    return (
      <Typography variant="body2" style={{ color: "#f44336" }}>
        Falha ao calcular a estimativa de custo.
      </Typography>
    );
  }

  const totalNecessario = estimate?.totalNecessario ?? null;
  const saldoAtual = estimate?.saldo ?? saldo;
  const insuficiente =
    totalNecessario !== null && saldoAtual !== null && totalNecessario > saldoAtual;

  return (
    <>
      <Box display="flex" alignItems="center" style={{ gap: 8 }}>
        <Typography
          variant="body2"
          style={insuficiente ? { color: "#f44336", fontWeight: "bold" } : undefined}
          color={insuficiente ? undefined : "textSecondary"}
        >
          {`Saldo: ${saldoAtual ?? "-"} • Necessário: ${totalNecessario ?? "-"}`}
        </Typography>
        <Button
          size="small"
          color="primary"
          onClick={() => setComprarCreditosOpen(true)}
        >
          Comprar créditos
        </Button>
      </Box>
      {estimate?.pesoRota > 1 && (
        <Typography variant="body2" color="textSecondary">
          {tc("smsRota.estimateWeight", { count: estimate.pesoRota })}
        </Typography>
      )}
      {estimate?.fixos > 0 && (
        <Typography variant="body2" color="textSecondary">
          {i18n.t("campaigns.smsEstimate.landlineSkipped", { count: estimate.fixos })}
        </Typography>
      )}
      {saldoError && saldoAtual === null && (
        <Typography variant="body2" style={{ color: "#f44336" }}>
          Falha ao consultar o saldo de SMS.
        </Typography>
      )}
      <ComprarCreditosSmsModal
        open={comprarCreditosOpen}
        onClose={() => setComprarCreditosOpen(false)}
        onSaldoAtualizado={saldoNovo => setSaldo(saldoNovo)}
      />
    </>
  );
};

const CampaignModal = ({
  open,
  onClose,
  campaignId,
  initialValues,
  onSave,
  resetPagination,
}) => {
  const classes = useStyles();
  const isMounted = useRef(true);
  const { user, socket } = useContext(AuthContext);
  const { companyId } = user;

  const initialState = {
    name: "",
    message1: "",
    message2: "",
    message3: "",
    message4: "",
    message5: "",
    confirmationMessage1: "",
    confirmationMessage2: "",
    confirmationMessage3: "",
    confirmationMessage4: "",
    confirmationMessage5: "",
    status: "INATIVA",
    confirmation: false,
    scheduledAt: "",
    contactListId: "",
    tagListId: "Nenhuma",
    companyId,
    statusTicket: "closed",
    openTicket: "disabled",
    // Novos campos de recorrência
    isRecurring: false,
    recurrenceType: "",
    recurrenceInterval: 1,
    recurrenceDaysOfWeek: [],
    recurrenceDayOfMonth: 1,
    recurrenceEndDate: "",
    maxExecutions: null,
    // Campanha SMS
    canal: "whatsapp",
    smsDestinoWhatsappId: "",
    smsDestinoUrl: "",
    smsTextoPrePreenchido: "",
    smsRota: "marketing",
  };

  const [campaign, setCampaign] = useState(initialState);
  const [whatsapps, setWhatsapps] = useState([]);
  const [selectedWhatsapps, setSelectedWhatsapps] = useState([]);
 const [whatsappId, setWhatsappId] = useState(null);
  const [canal, setCanal] = useState("whatsapp");
  const [contactLists, setContactLists] = useState([]);
  const [tagLists, setTagLists] = useState([]);
  const [messageTab, setMessageTab] = useState(0);
  const [attachment, setAttachment] = useState(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [campaignEditable, setCampaignEditable] = useState(true);
  const [previewExecutions, setPreviewExecutions] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const attachmentFile = useRef(null);

  const [smsDestinoTipo, setSmsDestinoTipo] = useState("whatsapp");
  const [rotasSms, setRotasSms] = useState([{ rota: "marketing", peso: 1 }]);

  // Menu "Adicionar variável" dos campos de mensagem (SMS e WhatsApp):
  // um menu compartilhado, ancorado no botão do campo que o abriu, que
  // insere o token na posição do cursor (não no fim) e devolve o foco.
  const [menuVariaveis, setMenuVariaveis] = useState({ anchor: null, campo: null });
  const camposMensagemRefs = useRef({});

  const insereVariavel = (token, values, setFieldValue) => {
    const campo = menuVariaveis.campo;
    if (!campo) return;
    const input = camposMensagemRefs.current[campo];
    const texto = values[campo] || "";
    const inicio = input?.selectionStart ?? texto.length;
    const fim = input?.selectionEnd ?? texto.length;
    setFieldValue(campo, `${texto.slice(0, inicio)}${token}${texto.slice(fim)}`);
    setMenuVariaveis({ anchor: null, campo: null });
    setTimeout(() => {
      if (!input) return;
      input.focus();
      const pos = inicio + token.length;
      input.setSelectionRange(pos, pos);
    }, 0);
  };

  const VARIAVEIS_MENSAGEM = [
    { token: "{nome}", rotulo: "Nome do contato" },
    { token: "{numero}", rotulo: "Número do contato" },
    { token: "{email}", rotulo: "E-mail do contato" },
  ];

  const [templates, setTemplates] = useState([]);
  // Quantos templates aprovados a conexao tem antes do filtro de campanha —
  // e o que separa "sincronize os templates" de "os templates existem mas
  // nenhum serve para campanha".
  const [totalAprovados, setTotalAprovados] = useState(0);
  const [quickMessageId, setQuickMessageId] = useState(null);
  const [templateParams, setTemplateParams] = useState([]);
  const [templateHeaderMediaUrl, setTemplateHeaderMediaUrl] = useState("");
  const [enviandoMidiaTemplate, setEnviandoMidiaTemplate] = useState(false);

  // Upload da midia do cabecalho: o backend grava em public/ e devolve a URL
  // publica, que entra no campo como se o usuario tivesse colado.
  const handleUploadHeaderMedia = async (arquivo, formato) => {
    setEnviandoMidiaTemplate(true);
    try {
      const formData = new FormData();
      formData.append("formato", formato);
      formData.append("file", arquivo);
      const { data } = await api.post("/campaigns/template-media", formData);
      setTemplateHeaderMediaUrl(data?.url || "");
      toast.success(i18n.t("aeros.campaign.template.uploadSuccess"));
    } catch (err) {
      toastError(err);
    } finally {
      setEnviandoMidiaTemplate(false);
    }
  };
  // Guarda o whatsappId do render anterior para distinguir "usuário trocou
  // de conexão" (deve limpar template/params) de "hidratação inicial de uma
  // campanha existente" (null -> id vindo do /campaigns/:id, cujo próprio
  // template/params já vêm no mesmo payload e não podem ser apagados).
  const whatsappIdAnteriorRef = useRef(null);

  const [options, setOptions] = useState([]);
  const [queues, setQueues] = useState([]);
  const [allQueues, setAllQueues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedQueue, setSelectedQueue] = useState(null);
  const { findAll: findAllQueues } = useQueues();

  const conexaoSelecionada = whatsapps.find((w) => w.id === whatsappId);
  const ehSms = canal === "sms";
  // SMS não usa a conexão de disparo: a conexão oficial escolhida antes de
  // trocar para SMS não pode manter o bloco de template no lugar do texto.
  const ehOficial = !ehSms && conexaoSelecionada?.channel === "whatsapp_oficial";

  // Opções para dias da semana
  const daysOfWeekOptions = [
    { value: 0, label: 'Domingo' },
    { value: 1, label: 'Segunda-feira' },
    { value: 2, label: 'Terça-feira' },
    { value: 3, label: 'Quarta-feira' },
    { value: 4, label: 'Quinta-feira' },
    { value: 5, label: 'Sexta-feira' },
    { value: 6, label: 'Sábado' },
  ];

  // Função para preview das execuções
  const handlePreviewRecurrence = async (values) => {
    if (!values.isRecurring || !values.recurrenceType || !values.scheduledAt) {
      setPreviewExecutions([]);
      return;
    }

    try {
      const params = {
        recurrenceType: values.recurrenceType,
        recurrenceInterval: values.recurrenceInterval,
        recurrenceDaysOfWeek: JSON.stringify(values.recurrenceDaysOfWeek),
        recurrenceDayOfMonth: values.recurrenceDayOfMonth,
      };

      if (campaignId) {
        const { data } = await api.get(`/campaigns/${campaignId}/recurrence-preview`, { params });
        setPreviewExecutions(data.executions);
      } else {
        // Para campanhas novas, calcular localmente ou usar endpoint genérico
        const mockExecutions = calculateMockExecutions(values);
        setPreviewExecutions(mockExecutions);
      }
    } catch (err) {
      console.error('Erro ao buscar preview:', err);
    }
  };

  // Função auxiliar para calcular execuções mock
  const calculateMockExecutions = (values) => {
    const executions = [];
    let currentDate = moment(values.scheduledAt);
    
    for (let i = 0; i < 5; i++) {
      executions.push(currentDate.format('DD/MM/YYYY HH:mm'));
      
      switch (values.recurrenceType) {
        case 'minutely':
          currentDate = currentDate.clone().add(values.recurrenceInterval, 'minutes');
          break;
        case 'hourly':
          currentDate = currentDate.clone().add(values.recurrenceInterval, 'hours');
          break;
        case 'daily':
          currentDate = currentDate.clone().add(values.recurrenceInterval, 'days');
          break;
        case 'weekly':
          currentDate = currentDate.clone().add(values.recurrenceInterval, 'weeks');
          break;
        case 'biweekly':
          currentDate = currentDate.clone().add(values.recurrenceInterval * 2, 'weeks');
          break;
        case 'monthly':
          currentDate = currentDate.clone().add(values.recurrenceInterval, 'months');
          if (values.recurrenceDayOfMonth) {
            currentDate = currentDate.date(values.recurrenceDayOfMonth);
          }
          break;
        case 'yearly':
          currentDate = currentDate.clone().add(values.recurrenceInterval, 'years');
          break;
      }
    }
    
    return executions;
  };

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (isMounted.current) {
      const loadQueues = async () => {
        const list = await findAllQueues();
        setAllQueues(list);
        setQueues(list);
      };
      loadQueues();
    }
  }, []);

  useEffect(() => {
    if (searchParam.length < 3) {
      setLoading(false);
      setSelectedQueue("");
      return;
    }
    const delayDebounceFn = setTimeout(() => {
      setLoading(true);
      const fetchUsers = async () => {
        try {
          const { data } = await api.get("/users/");
          setOptions(data.users);
          setLoading(false);
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchUsers();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam]);

  useEffect(() => {
    if (isMounted.current) {
      if (initialValues) {
        setCampaign((prevState) => {
          return { ...prevState, ...initialValues };
        });
      }

      api
        .get(`/contact-lists/list`, { params: { companyId } })
        .then(({ data }) => setContactLists(data));

      api
        .get(`/whatsapp`, { params: { companyId, session: 0 } })
        .then(({ data }) => {
          const mappedWhatsapps = data.map((whatsapp) => ({
            ...whatsapp,
            selected: false,
          }));
          setWhatsapps(mappedWhatsapps);
        });

      api.get(`/tags/list`, { params: { companyId, kanban: 0 } })
        .then(({ data }) => {
          const fetchedTags = data;
          const formattedTagLists = fetchedTags
            .filter(tag => tag.contacts.length > 0)
            .map((tag) => ({
              id: tag.id,
              name: `${tag.name} (${tag.contacts.length})`,
            }));
          setTagLists(formattedTagLists);
        })
        .catch((error) => {
          console.error("Error retrieving tags:", error);
        });

      if (!campaignId) return;

      api.get(`/campaigns/${campaignId}`).then(({ data }) => {
        if (data?.user) setSelectedUser(data.user);
        if (data?.queue) setSelectedQueue(data.queue.id);
        if (data?.whatsappId) {
          setWhatsappId(parseInt(data.whatsappId)); // Converter para número
        } else {
          setWhatsappId(null);
        }

        if (data?.quickMessageId) {
          setQuickMessageId(parseInt(data.quickMessageId));
        } else {
          setQuickMessageId(null);
        }

        setCanal(data?.canal === "sms" ? "sms" : "whatsapp");
        setSmsDestinoTipo(data?.smsDestinoUrl ? "url" : "whatsapp");

        try {
          const params = JSON.parse(data?.templateParams || "[]");
          setTemplateParams(Array.isArray(params) ? params : []);
        } catch {
          setTemplateParams([]);
        }
        setTemplateHeaderMediaUrl(data?.templateHeaderMediaUrl || "");

        setCampaign((prev) => {
          let prevCampaignData = Object.assign({}, prev);

          Object.entries(data).forEach(([key, value]) => {
            if (key === "scheduledAt" && value !== "" && value !== null) {
              prevCampaignData[key] = moment(value).format("YYYY-MM-DDTHH:mm");
            } else if (key === "recurrenceEndDate" && value !== "" && value !== null) {
              prevCampaignData[key] = moment(value).format("YYYY-MM-DD");
            } else if (key === "recurrenceDaysOfWeek" && value) {
              prevCampaignData[key] = JSON.parse(value);
            } else {
              prevCampaignData[key] = value === null ? "" : value;
            }
          });

          return prevCampaignData;
        });
      });
    }
  }, [campaignId, open, initialValues, companyId]);

  useEffect(() => {
    const now = moment();
    const scheduledAt = moment(campaign.scheduledAt);
    // Editavel ate perto do disparo. A margem de 5 minutos evita corrida com o
    // verificador do backend, que captura a campanha (PROGRAMADA ->
    // EM_ANDAMENTO) ate 1 minuto antes do horario agendado.
    const foraDaJanelaDeDisparo =
      !Number.isNaN(scheduledAt.diff(now)) &&
      scheduledAt.diff(now, "minutes") > 5;
    const isEditable =
      campaign.status === "INATIVA" ||
      (campaign.status === "PROGRAMADA" && foraDaJanelaDeDisparo);

    setCampaignEditable(isEditable);
  }, [campaign.status, campaign.scheduledAt]);

  useEffect(() => {
    const whatsappIdAnterior = whatsappIdAnteriorRef.current;
    whatsappIdAnteriorRef.current = whatsappId;

    // Depende só de whatsappId — não de ehOficial — para não reagir à lista
    // de conexões chegando depois via rede e apagar um template recém-hidratado.
    if (deveLimparTemplateNaTrocaDeConexao(whatsappIdAnterior, whatsappId)) {
      setQuickMessageId(null);
      setTemplateParams([]);
      setTemplateHeaderMediaUrl("");
    }
  }, [whatsappId]);

  useEffect(() => {
    if (!ehOficial || !whatsappId) {
      setTemplates([]);
      setTotalAprovados(0);
      return;
    }

    api
      .get(`/whatsapp/${whatsappId}/templates?incluirMidiaHeader=1`)
      .then(({ data }) => {
        setTemplates(data?.templates || []);
        setTotalAprovados(data?.totalAprovados || 0);
      })
      .catch((err) => {
        setTemplates([]);
        setTotalAprovados(0);
        toastError(err);
      });
  }, [whatsappId, ehOficial]);

  const handleClose = () => {
    onClose();
    setCampaign(initialState);
    setPreviewExecutions([]);
    setShowPreview(false);
    setTemplates([]);
    setTotalAprovados(0);
    setQuickMessageId(null);
    setTemplateParams([]);
    setTemplateHeaderMediaUrl("");
    setCanal("whatsapp");
    setSmsDestinoTipo("whatsapp");
  };

  const handleAttachmentFile = (e) => {
    const file = head(e.target.files);
    if (file) {
      setAttachment(file);
    }
  };

const handleSaveCampaign = async (values) => {
  // Confirmação habilitada sem nenhuma mensagem de confirmação faria o
  // disparo enviar o literal "null" ao contato (o backend também rejeita;
  // aqui o aviso chega antes do request). Toast em vez de erro de campo:
  // os campos de confirmação ficam em abas e não exibem helperText.
  if (values.confirmation) {
    const temMensagemConfirmacao = [
      values.confirmationMessage1,
      values.confirmationMessage2,
      values.confirmationMessage3,
      values.confirmationMessage4,
      values.confirmationMessage5,
    ].some((m) => m && m.trim());

    if (!temMensagemConfirmacao) {
      toast.error(
        "A confirmação está habilitada, mas nenhuma mensagem de confirmação foi preenchida. Preencha ao menos uma ou desabilite a confirmação."
      );
      return;
    }
  }

  if (ehOficial) {
    const selecionado = templates.find((t) => t.id === quickMessageId);
    const erroUrl = erroUrlMidiaCabecalho(selecionado, templateHeaderMediaUrl);
    if (erroUrl) {
      toast.error(i18n.t(`aeros.campaign.template.mediaUrl${erroUrl === "required" ? "Required" : "Invalid"}`));
      return;
    }
  }

  try {
    const dataValues = {
      ...values,
      canal,
      whatsappId: ehSms ? null : whatsappId,
      userId: selectedUser?.id || null,
      queueId: selectedQueue || null,
      quickMessageId: ehOficial ? quickMessageId : null,
      templateParams: ehOficial ? JSON.stringify(templateParams) : null,
      templateHeaderMediaUrl: ehOficial && templateHeaderMediaUrl.trim() ? templateHeaderMediaUrl.trim() : null,
      // CORREÇÃO: Garantir conversão correta do array
      recurrenceDaysOfWeek: (values.isRecurring && values.recurrenceDaysOfWeek && values.recurrenceDaysOfWeek.length > 0)
        ? values.recurrenceDaysOfWeek // Enviar array, o backend irá converter para JSON
        : null, // Enviar null se não for recorrente ou array vazio
    };

    // Processar datas
    Object.entries(values).forEach(([key, value]) => {
      if (key === "scheduledAt" && value !== "" && value !== null) {
        dataValues[key] = moment(value).format("YYYY-MM-DD HH:mm:ss");
      } else if (key === "recurrenceEndDate" && value !== "" && value !== null) {
        dataValues[key] = moment(value).format("YYYY-MM-DD HH:mm:ss");
      } else if (key !== "recurrenceDaysOfWeek") { // Não processar recurrenceDaysOfWeek aqui
        dataValues[key] = value === "" ? null : value;
      }
    });

    // Garantir que campos não recorrentes sejam null quando isRecurring é false
    if (!values.isRecurring) {
      dataValues.recurrenceType = null;
      dataValues.recurrenceInterval = null;
      dataValues.recurrenceDaysOfWeek = null;
      dataValues.recurrenceDayOfMonth = null;
      dataValues.recurrenceEndDate = null;
      dataValues.maxExecutions = null;
    }

    // Destino do link SMS: só o grupo escolhido (rádio) é enviado; o outro é
    // zerado para não deixar lixo de uma troca de opção no formulário.
    if (ehSms) {
      dataValues.smsDestinoWhatsappId =
        smsDestinoTipo === "whatsapp" ? values.smsDestinoWhatsappId || null : null;
      dataValues.smsDestinoUrl =
        smsDestinoTipo === "url" ? values.smsDestinoUrl || null : null;
      dataValues.smsRota = values.smsRota || "marketing";
    } else {
      delete dataValues.smsRota;
      dataValues.smsDestinoWhatsappId = null;
      dataValues.smsDestinoUrl = null;
      dataValues.smsTextoPrePreenchido = null;
    }

    if (campaignId) {
      await api.put(`/campaigns/${campaignId}`, dataValues);
      if (attachment != null) {
        const formData = new FormData();
        formData.append("file", attachment);
        await api.post(`/campaigns/${campaignId}/media-upload`, formData);
      }
      handleClose();
    } else {
      const { data } = await api.post("/campaigns", dataValues);
      if (attachment != null) {
        const formData = new FormData();
        formData.append("file", attachment);
        await api.post(`/campaigns/${data.id}/media-upload`, formData);
      }
      if (onSave) {
        onSave(data);
      }
      handleClose();
    }
    toast.success(i18n.t("campaigns.toasts.success"));
  } catch (err) {
    console.log(err);
    toastError(err);
  }
};

  const deleteMedia = async () => {
    if (attachment) {
      setAttachment(null);
      attachmentFile.current.value = null;
    }

    if (campaign.mediaPath) {
      await api.delete(`/campaigns/${campaign.id}/media-upload`);
      setCampaign((prev) => ({ ...prev, mediaPath: null, mediaName: null }));
      toast.success(i18n.t("campaigns.toasts.deleted"));
    }
  };

  const cancelCampaign = async () => {
    try {
      await api.post(`/campaigns/${campaign.id}/cancel`);
      toast.success(i18n.t("campaigns.toasts.cancel"));
      setCampaign((prev) => ({ ...prev, status: "CANCELADA" }));
      resetPagination();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const restartCampaign = async () => {
    try {
      await api.post(`/campaigns/${campaign.id}/restart`);
      toast.success(i18n.t("campaigns.toasts.restart"));
      setCampaign((prev) => ({ ...prev, status: "EM_ANDAMENTO" }));
      resetPagination();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const filterOptions = createFilterOptions({ trim: true });
  const m = useAerosModalStyles();
  const aeros = useAerosSettingsStyles();

  // ---- só apresentação (Aeros) ----
  const [confTab, setConfTab] = useState(0);
  const [emojiAnchor, setEmojiAnchor] = useState(null);
  const [emojiField, setEmojiField] = useState(null);
  const [settings, setSettings] = useState({});
  const [listCount, setListCount] = useState(null);
  const [previewContact, setPreviewContact] = useState(null);
  const [testOpen, setTestOpen] = useState(false);
  const [testNumber, setTestNumber] = useState(localStorage.getItem("campaignTestNumber") || "");
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/campaign-settings").then(({ data }) => {
      const s = {};
      (Array.isArray(data) ? data : []).forEach((it) => { s[it.key] = it.value; });
      const parseDays = (v) => { try { const p = JSON.parse(v); return Array.isArray(p) ? p.map(Number) : null; } catch { return null; } };
      setSettings({
        messageInterval: Number(s.messageInterval) || 20,
        longerIntervalAfter: Number(s.longerIntervalAfter) || 20,
        greaterInterval: Number(s.greaterInterval) || 60,
        dailyLimit: Number(String(s.dailyLimit || 0).replace(/"/g, "")) || 0,
        // null = nunca configurado: o SMS herda o limite do WhatsApp.
        dailyLimitSms: s.dailyLimitSms === undefined || s.dailyLimitSms === null ? null : Number(String(s.dailyLimitSms).replace(/"/g, "")) || 0,
        startHour: s.startHour || null,
        endHour: s.endHour || null,
        sendingDays: parseDays(s.sendingDays) || undefined
      });
    }).catch(() => {});
  }, [open]);

  const loadListInfo = (contactListId) => {
    if (!contactListId) { setListCount(null); setPreviewContact(null); return; }
    api.get("/contact-list-items", { params: { contactListId, pageNumber: 1 } }).then(({ data }) => {
      setListCount(data?.count ?? null);
      setPreviewContact(data?.contacts?.[0] || null);
    }).catch(() => { setListCount(null); setPreviewContact(null); });
  };
  // Só carrega a lista da campanha aberta. A escolha no formulário chama
  // loadListInfo direto: gravar em `campaign` (initialValues com
  // enableReinitialize) reiniciava o Formik e apagava o que já foi digitado.
  // Rotas SMS disponíveis (Premium só aparece se o super admin configurou).
  useEffect(() => {
    if (!open || !ehSms) return;
    api.get("/sms/rotas").then(({ data }) => {
      if (isMounted.current && Array.isArray(data) && data.length) setRotasSms(data);
    }).catch(() => {});
  }, [open, ehSms]);
  useEffect(() => { loadListInfo(campaign.contactListId); }, [campaign.contactListId]); // eslint-disable-line react-hooks/exhaustive-deps

  const disabled = !campaignEditable && campaign.status !== "CANCELADA";
  const editable = campaignEditable;

  const insertAtCursor = (campo, token, values, setFieldValue) => {
    const input = camposMensagemRefs.current[campo];
    const texto = values[campo] || "";
    const inicio = input?.selectionStart ?? texto.length;
    const fim = input?.selectionEnd ?? texto.length;
    setFieldValue(campo, `${texto.slice(0, inicio)}${token}${texto.slice(fim)}`);
    setTimeout(() => { if (!input) return; input.focus(); const pos = inicio + token.length; input.setSelectionRange(pos, pos); }, 0);
  };
  const wrapSelection = (campo, mark, values, setFieldValue) => {
    const input = camposMensagemRefs.current[campo];
    const texto = values[campo] || "";
    const a = input?.selectionStart ?? texto.length, b = input?.selectionEnd ?? texto.length;
    if (a === b) return insertAtCursor(campo, `${mark}${mark}`, values, setFieldValue);
    setFieldValue(campo, `${texto.slice(0, a)}${mark}${texto.slice(a, b)}${mark}${texto.slice(b)}`);
  };

  const sendTest = async (values) => {
    const number = String(testNumber || "").replace(/\D/g, "");
    if (!number) return;
    // Oficial: o teste manda o template, entao a URL da midia precisa estar
    // certa antes do request (mesma regra do salvamento).
    if (ehOficial) {
      const selecionado = templates.find((tpl) => tpl.id === quickMessageId);
      const erroUrl = erroUrlMidiaCabecalho(selecionado, templateHeaderMediaUrl);
      if (erroUrl) {
        toast.error(i18n.t(`aeros.campaign.template.mediaUrl${erroUrl === "required" ? "Required" : "Invalid"}`));
        return;
      }
    }
    setTesting(true);
    try {
      localStorage.setItem("campaignTestNumber", number);
      await api.post("/campaigns/test", {
        whatsappId,
        canal,
        number,
        message: values.message1 || values.message2 || "",
        mediaPath: campaign.mediaPath || null,
        mediaName: campaign.mediaName || null,
        quickMessageId: ehOficial ? quickMessageId : null,
        templateParams: ehOficial ? templateParams : null,
        templateHeaderMediaUrl: ehOficial && templateHeaderMediaUrl.trim() ? templateHeaderMediaUrl.trim() : null,
        statusTicket: values.statusTicket || "closed",
        queueId: selectedQueue || null,
        campaignId: campaignId || null,
        smsRota: ehSms ? values.smsRota || "marketing" : undefined,
      });
      toast.success(tc("testSent", { number }));
      setTestOpen(false);
    } catch (err) {
      toastError(err);
    } finally {
      setTesting(false);
    }
  };

  const stepsOf = (values) => {
    const configOk = Boolean(values.name && (values.contactListId || values.tagListId) && (ehSms || whatsappId) && values.scheduledAt);
    const msgOk = ehOficial ? Boolean(quickMessageId) : filledMessages(values).some(Boolean);
    return [
      { key: "config", state: configOk ? "done" : "active" },
      { key: "messages", state: msgOk ? "done" : configOk ? "active" : "todo" },
      { key: "review", state: configOk && msgOk ? "active" : "todo" },
    ];
  };

  const Editor = ({ name, values, setFieldValue, placeholder, minHeight }) => (
    <div className={classes.editor}>
      <textarea
        className={classes.textarea}
        style={minHeight ? { minHeight } : undefined}
        name={name}
        value={values[name] || ""}
        placeholder={placeholder}
        disabled={disabled}
        ref={(el) => (camposMensagemRefs.current[name] = el)}
        onChange={(e) => setFieldValue(name, e.target.value)}
      />
      <div className={classes.editorBar}>
        {(ehSms ? [...MESSAGE_VARS.slice(0, 1), { token: "{link}", key: "link" }] : MESSAGE_VARS).map((v) => (
          <button key={v.token} type="button" className={classes.varChip} onClick={() => insertAtCursor(name, v.token, values, setFieldValue)} disabled={disabled}>{v.token}</button>
        ))}
        <div className={classes.barIcons}>
          <IconButton size="small" onClick={(e) => { setEmojiAnchor(e.currentTarget); setEmojiField(name); }} disabled={disabled} aria-label="Emoji"><MoodIcon /></IconButton>
          {!ehSms && <IconButton size="small" onClick={() => wrapSelection(name, "*", values, setFieldValue)} disabled={disabled} aria-label="Negrito"><FormatBoldIcon /></IconButton>}
          {!ehSms && <IconButton size="small" onClick={() => wrapSelection(name, "_", values, setFieldValue)} disabled={disabled} aria-label="Itálico"><FormatItalicIcon /></IconButton>}
        </div>
        <span className={classes.count}>{(values[name] || "").length}</span>
      </div>
    </div>
  );

  return (
    <div className={classes.root}>
      <ConfirmationModal
        title={i18n.t("campaigns.confirmationModal.deleteTitle")}
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        onConfirm={deleteMedia}
      >
        {i18n.t("campaigns.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <Dialog open={open} onClose={handleClose} maxWidth={false} classes={{ paper: classes.paper }} scroll="paper">
        {!ehOficial && !ehSms && (
          <div style={{ display: "none" }}>
            <input type="file" ref={attachmentFile} onChange={(e) => handleAttachmentFile(e)} />
          </div>
        )}
        <Formik
          initialValues={campaign}
          enableReinitialize={true}
          validationSchema={CampaignSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => { handleSaveCampaign(values); actions.setSubmitting(false); }, 400);
          }}
        >
          {({ values, errors, touched, isSubmitting, setFieldValue }) => {
            const steps = stepsOf(values);
            const filled = filledMessages(values);
            const previewText = previewMessage(values[`message${messageTab + 1}`] || values.message1, previewContact);
            const contactsForEstimate = listCount ?? (values.tagListId ? Number((tagLists.find((t) => String(t.id) === String(values.tagListId))?.name.match(/\((\d+)\)/) || [])[1]) || null : null);
            return (
              <Form className={m.form}>
                <div className={`${m.header} ${classes.header}`}>
                  <div>
                    <div className={m.title}>{!editable ? tc("readonly") : campaignId ? tc("edit") : tc("new")}</div>
                    <div className={m.subtitle}>{tc("subtitle")}</div>
                  </div>
                  <div className={classes.segmented} role="tablist">
                    <button type="button" className={`${classes.seg} ${!ehSms ? classes.segOn : ""}`} disabled={!editable} onClick={() => { setFieldValue("canal", "whatsapp"); setCanal("whatsapp"); }}><WhatsAppIcon style={{ color: "#25d366" }} />WhatsApp</button>
                    <button type="button" className={`${classes.seg} ${ehSms ? classes.segOn : ""}`} disabled={!editable} onClick={() => { setFieldValue("canal", "sms"); setCanal("sms"); }}><SmsOutlinedIcon />SMS</button>
                  </div>
                  <IconButton className={m.closeButton} onClick={handleClose} aria-label={tc("close")} style={{ marginTop: 6 }}><CloseIcon /></IconButton>
                </div>

                <div className={classes.steps}>
                  {steps.map((s, i) => (
                    <div key={s.key} className={`${classes.step} ${s.state === "active" ? classes.stepOn : s.state === "done" ? classes.stepDone : ""}`}>
                      <div className={`${classes.dot} ${s.state === "active" ? classes.dotOn : s.state === "done" ? classes.dotDone : ""}`}>{s.state === "done" ? <CheckIcon /> : i + 1}</div>
                      {tc(`steps.${s.key}`)}
                    </div>
                  ))}
                </div>

                <div className={classes.grid}>
                  {/* ---------- Coluna esquerda ---------- */}
                  <div className={classes.left}>
                    <div>
                      <label className={classes.label} htmlFor="campaign-name">{tc("name")}</label>
                      <div className={`${classes.field} ${touched.name && errors.name ? classes.fieldError : ""}`}>
                        <Field id="campaign-name" name="name" placeholder={tc("namePlaceholder")} disabled={!editable} />
                      </div>
                      {touched.name && errors.name && <div className={classes.errorText}>{errors.name}</div>}
                    </div>
                    <div className={classes.row2}>
                      <div>
                        <label className={classes.label}>{tc("contactList")}</label>
                        <div className={classes.field}>
                          <ListAltIcon style={{ color: AEROS.cyan }} />
                          <Select native disableUnderline className={classes.sel} value={values.contactListId || ""} disabled={!editable} onChange={(e) => { const v = e.target.value; setFieldValue("contactListId", v); loadListInfo(v); if (v) setFieldValue("tagListId", ""); }} inputProps={{ "aria-label": tc("contactList") }}>
                            <option value="">{tc("none")}</option>
                            {contactLists.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                          </Select>
                          {listCount != null && <span className={classes.count}>{listCount.toLocaleString("pt-BR")}</span>}
                        </div>
                      </div>
                      <div>
                        <label className={classes.label}>{tc("orTag")}</label>
                        <div className={classes.field}>
                          <LocalOfferOutlinedIcon />
                          <Select native disableUnderline className={classes.sel} value={values.tagListId || ""} disabled={!editable} onChange={(e) => { setFieldValue("tagListId", e.target.value); if (e.target.value) { setFieldValue("contactListId", ""); loadListInfo(""); } }} inputProps={{ "aria-label": tc("orTag") }}>
                            <option value="">{tc("none")}</option>
                            {tagLists.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                          </Select>
                        </div>
                      </div>
                    </div>
                    <div className={classes.row2}>
                      {!ehSms && (
                        <div>
                          <label className={classes.label}>{tc("connection")}</label>
                          <div className={classes.field}>
                            <span className={classes.conn} style={{ backgroundColor: conexaoSelecionada?.status === "CONNECTED" ? AEROS.success : conexaoSelecionada ? AEROS.warning : "rgba(128,128,128,0.4)" }} />
                            <Select native disableUnderline className={classes.sel} value={whatsappId || ""} disabled={!editable} onChange={(e) => setWhatsappId(e.target.value ? Number(e.target.value) : null)} inputProps={{ "aria-label": tc("connection") }}>
                              <option value="">{tc("selectConnection")}</option>
                              {whatsapps.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                            </Select>
                          </div>
                        </div>
                      )}
                      <div>
                        <label className={classes.label} htmlFor="campaign-scheduledAt">{tc("scheduleFor")}</label>
                        <div className={`${classes.field} ${touched.scheduledAt && errors.scheduledAt ? classes.fieldError : ""}`}>
                          <EventIcon />
                          <Field id="campaign-scheduledAt" name="scheduledAt" type="datetime-local" disabled={!editable} />
                        </div>
                      </div>
                    </div>

                    {/* Recorrência */}
                    <div className={classes.card}>
                      <div className={classes.cardHead}>
                        <RepeatIcon />
                        <div style={{ flex: 1 }}><div className={classes.cardTitle}>{tc("recurrenceTitle")}</div><div className={classes.cardSub}>{tc("recurrenceDesc")}</div></div>
                        <Switch className={aeros.toggleSwitchSmall} checked={Boolean(values.isRecurring)} disabled={!editable} onChange={(e) => { setFieldValue("isRecurring", e.target.checked); if (!e.target.checked) setPreviewExecutions([]); }} inputProps={{ "aria-label": tc("recurrenceTitle") }} />
                      </div>
                      {values.isRecurring && (
                        <>
                          <div className={classes.recGrid}>
                            <div>
                              <span className={classes.small}>{tc("type")}</span>
                              <div className={`${classes.field} ${classes.fieldSm}`}>
                                <Select native disableUnderline className={classes.sel} value={values.recurrenceType || ""} disabled={!editable} onChange={(e) => { setFieldValue("recurrenceType", e.target.value); setFieldValue("recurrenceDaysOfWeek", []); setFieldValue("recurrenceDayOfMonth", 1); }} inputProps={{ "aria-label": tc("type") }}>
                                  <option value="">—</option>
                                  {RECURRENCE_TYPES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                                </Select>
                              </div>
                            </div>
                            <div>
                              <span className={classes.small}>{tc("interval")}</span>
                              <div className={`${classes.field} ${classes.fieldSm}`}><Field name="recurrenceInterval" type="number" min={1} disabled={!editable} /></div>
                            </div>
                            <div>
                              <span className={classes.small}>{tc("endDate")}</span>
                              <div className={`${classes.field} ${classes.fieldSm}`}><Field name="recurrenceEndDate" type="date" disabled={!editable} placeholder={tc("optional")} /></div>
                            </div>
                            <div>
                              <span className={classes.small}>{values.recurrenceType === "monthly" ? tc("dayOfMonth") : tc("maxExecutions")}</span>
                              <div className={`${classes.field} ${classes.fieldSm}`}>
                                {values.recurrenceType === "monthly"
                                  ? <Field name="recurrenceDayOfMonth" type="number" min={1} max={31} disabled={!editable} />
                                  : <Field name="maxExecutions" type="number" min={1} disabled={!editable} placeholder={tc("optional")} />}
                              </div>
                            </div>
                          </div>
                          {values.recurrenceType === "weekly" && (
                            <div className={classes.days}>
                              {WEEKDAY_SHORT().map((d, i) => {
                                const on = (values.recurrenceDaysOfWeek || []).includes(i);
                                return <button key={i} type="button" className={`${classes.day} ${on ? classes.dayOn : ""}`} disabled={!editable} aria-pressed={on} aria-label={d} onClick={() => setFieldValue("recurrenceDaysOfWeek", on ? values.recurrenceDaysOfWeek.filter((x) => x !== i) : [...(values.recurrenceDaysOfWeek || []), i])}>{d}</button>;
                              })}
                            </div>
                          )}
                          {touched.recurrenceDaysOfWeek && errors.recurrenceDaysOfWeek && <div className={classes.errorText}>{errors.recurrenceDaysOfWeek}</div>}
                          {values.recurrenceType && values.scheduledAt && (
                            <div className={classes.info}><InfoOutlinedIcon />{nextExecutionsLabel(nextExecutions(values.scheduledAt, values, 4))}</div>
                          )}
                        </>
                      )}
                    </div>

                    {!ehSms && (
                      <>
                        <div className={classes.row3}>
                          {!ehOficial && (
                            <div>
                              <label className={classes.label}>{tc("readConfirmation")}</label>
                              <div className={classes.field}>
                                <Select native disableUnderline className={classes.sel} value={values.confirmation ? "1" : "0"} disabled={!editable} onChange={(e) => setFieldValue("confirmation", e.target.value === "1")} inputProps={{ "aria-label": tc("readConfirmation") }}>
                                  <option value="1">{tc("enabled")}</option>
                                  <option value="0">{tc("disabled")}</option>
                                </Select>
                              </div>
                            </div>
                          )}
                          <div>
                            <label className={classes.label}>{tc("openTicket")}</label>
                            <div className={classes.field}>
                              <Select native disableUnderline className={classes.sel} value={values.openTicket || "disabled"} disabled={!editable} onChange={(e) => setFieldValue("openTicket", e.target.value)} inputProps={{ "aria-label": tc("openTicket") }}>
                                <option value="enabled">{tc("yes")}</option>
                                <option value="disabled">{tc("no")}</option>
                              </Select>
                            </div>
                          </div>
                          <div>
                            <label className={classes.label}>{tc("ticketStatus")}</label>
                            <div className={classes.field}>
                              <Select native disableUnderline className={classes.sel} value={values.statusTicket || "pending"} disabled={!editable || values.openTicket === "disabled"} onChange={(e) => setFieldValue("statusTicket", e.target.value)} inputProps={{ "aria-label": tc("ticketStatus") }}>
                                <option value="pending">{tc("ticketPending")}</option>
                                <option value="open">{tc("ticketOpen")}</option>
                                <option value="closed">{tc("ticketClosed")}</option>
                              </Select>
                            </div>
                          </div>
                        </div>
                        {values.openTicket === "enabled" && (
                          <div className={classes.row2}>
                            <div>
                              <label className={classes.label}>{tc("agent")}</label>
                              <Autocomplete
                                size="small"
                                getOptionLabel={(option) => `${option.name}`}
                                value={selectedUser}
                                onChange={(e, newValue) => {
                                  setSelectedUser(newValue);
                                  if (newValue != null && Array.isArray(newValue.queues)) {
                                    if (newValue.queues.length === 1) setSelectedQueue(newValue.queues[0].id);
                                    setQueues(newValue.queues);
                                  } else { setQueues(allQueues); setSelectedQueue(""); }
                                }}
                                options={options}
                                filterOptions={filterOptions}
                                freeSolo
                                fullWidth
                                autoHighlight
                                disabled={!editable}
                                noOptionsText={i18n.t("transferTicketModal.noOptions")}
                                loading={loading}
                                renderInput={(params) => (
                                  <TextField {...params} variant="outlined" className={m.input} placeholder={i18n.t("transferTicketModal.fieldLabel")} onChange={(e) => setSearchParam(e.target.value)} />
                                )}
                              />
                            </div>
                            <div>
                              <label className={classes.label}>{tc("queue")}</label>
                              <div className={classes.field}>
                                <Select native disableUnderline className={classes.sel} value={selectedQueue || ""} disabled={!editable} onChange={(e) => setSelectedQueue(e.target.value)} inputProps={{ "aria-label": tc("queue") }}>
                                  <option value="">—</option>
                                  {queues.map((q) => <option key={q.id} value={q.id}>{q.name}</option>)}
                                </Select>
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* ---------- Coluna direita ---------- */}
                  <div className={classes.right}>
                    {ehOficial ? (
                      <TemplateFields
                        templates={templates}
                        totalAprovados={totalAprovados}
                        quickMessageId={quickMessageId}
                        templateParams={templateParams}
                        disabled={!editable}
                        templateHeaderMediaUrl={templateHeaderMediaUrl}
                        onChangeHeaderMediaUrl={setTemplateHeaderMediaUrl}
                        onUploadHeaderMedia={handleUploadHeaderMedia}
                        enviandoMidia={enviandoMidiaTemplate}
                        onChangeTemplate={(id) => { setQuickMessageId(id); setTemplateParams([]); setTemplateHeaderMediaUrl(""); }}
                        onChangeParam={(indice, valor) => setTemplateParams((prev) => { const p = [...prev]; p[indice] = valor; return p; })}
                      />
                    ) : ehSms ? (
                      <>
                        <div className={classes.msgHead}><div className={classes.label} style={{ marginBottom: 0 }}>{tc("smsMessage")}</div></div>
                        <Editor name="message1" values={values} setFieldValue={setFieldValue} placeholder="Digite o texto do SMS…" />
                        {(() => {
                          const corpo = expandeLinkParaContagem(values.message1);
                          const fracoes = calcularFracoesSms(corpo);
                          return <div className={classes.info} style={fracoes > 1 ? { color: AEROS.warning } : undefined}>{`${corpo.length}/160 caracteres — ${fracoes} SMS por contato`}</div>;
                        })()}
                        <div className={classes.card}>
                          <div className={classes.cardTitle}>Destino do link</div>
                          <div className={classes.days}>
                            {[["whatsapp", "Conexão WhatsApp"], ["url", "URL livre"]].map(([k, l]) => (
                              <button key={k} type="button" className={`${classes.day} ${smsDestinoTipo === k ? classes.dayOn : ""}`} disabled={!editable} onClick={() => setSmsDestinoTipo(k)}>{l}</button>
                            ))}
                          </div>
                          {smsDestinoTipo === "whatsapp" ? (
                            <div className={classes.row2}>
                              <div className={classes.field}>
                                <Select native disableUnderline className={classes.sel} value={values.smsDestinoWhatsappId || ""} disabled={!editable} onChange={(e) => setFieldValue("smsDestinoWhatsappId", e.target.value)} inputProps={{ "aria-label": "Conexão WhatsApp" }}>
                                  <option value="">Conexão oficial…</option>
                                  {whatsapps.filter((w) => w.channel === "whatsapp_oficial").map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                                </Select>
                              </div>
                              <div className={classes.field}><Field name="smsTextoPrePreenchido" placeholder="Texto pré-preenchido" disabled={!editable} /></div>
                            </div>
                          ) : (
                            <div className={classes.field}><Field name="smsDestinoUrl" placeholder="https://…" disabled={!editable} /></div>
                          )}
                        </div>
                        {(() => {
                          const { opcoes, mostrarSeletor, rotaIndisponivel } = opcoesRotaSms(rotasSms, values.smsRota);
                          if (!mostrarSeletor) return null;
                          return (
                            <div className={classes.card}>
                              <div className={classes.cardTitle}>{tc("smsRota.title")}</div>
                              <div className={classes.field}>
                                <Select native disableUnderline className={classes.sel} value={values.smsRota || "marketing"} disabled={!editable} onChange={(e) => setFieldValue("smsRota", e.target.value)} inputProps={{ "aria-label": tc("smsRota.title") }}>
                                  {opcoes.map((o) => <option key={o.rota} value={o.rota}>{o.peso ? `${tc(`smsRota.name.${o.rota}`)} · ${tc("smsRota.credits", { count: o.peso })}` : tc(`smsRota.name.${o.rota}`)}</option>)}
                                </Select>
                              </div>
                              <div className={classes.info}>{tc("smsRota.help")}</div>
                              {rotaIndisponivel && <div className={classes.info} role="alert" style={{ color: AEROS.warning }}>{tc("smsRota.unavailable")}</div>}
                            </div>
                          );
                        })()}
                        <div className={classes.card}>
                          <div className={classes.cardTitle}>Custo estimado</div>
                          <SmsCostPanel contactListId={values.contactListId} message1={values.message1} rota={values.smsRota} />
                        </div>
                      </>
                    ) : (
                      <>
                        <div className={classes.msgHead}>
                          <div className={classes.label} style={{ marginBottom: 0 }}>{tc("messages")}</div>
                          <div className={classes.cardSub}>{tc("messagesHint")}</div>
                          <div className={classes.msgTabs}>
                            {[0, 1, 2, 3, 4].map((i) => (
                              <button key={i} type="button" className={`${classes.tab} ${messageTab === i ? classes.tabOn : filled[i] ? classes.tabFilled : ""}`} onClick={() => setMessageTab(i)} aria-pressed={messageTab === i}>{i + 1}</button>
                            ))}
                          </div>
                        </div>
                        <Editor name={`message${messageTab + 1}`} values={values} setFieldValue={setFieldValue} placeholder={tc("messagePlaceholder")} />

                        {(campaign.mediaPath || attachment) ? (
                          <div className={classes.attach}>
                            <div className={classes.thumb}>{/\.(png|jpe?g|gif|webp)$/i.test(attachment?.name || campaign.mediaName || "") ? <ImageOutlinedIcon /> : <InsertDriveFileOutlinedIcon />}</div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div className={classes.cardTitle} style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{attachment ? attachment.name : campaign.mediaName}</div>
                              <div className={classes.cardSub}>{attachment ? `${(attachment.size / 1048576).toFixed(1)} MB · ` : ""}{tc("attachHint")}</div>
                            </div>
                            {editable && <IconButton size="small" onClick={() => setConfirmationOpen(true)} aria-label={tc("removeAttachment")}><DeleteOutlineIcon style={{ color: AEROS.error, fontSize: 18 }} /></IconButton>}
                          </div>
                        ) : editable && (
                          <div className={classes.attach}>
                            <button type="button" className={classes.attachBtn} onClick={() => attachmentFile.current.click()}><AttachFileIcon />{tc("attach")}</button>
                          </div>
                        )}

                        {values.confirmation && (
                          <>
                            <div className={classes.msgHead}>
                              <div className={classes.label} style={{ marginBottom: 0 }}>{tc("confirmMessages")}</div>
                              <div className={classes.cardSub}>{tc("confirmHint")}</div>
                              <div className={classes.msgTabs}>
                                {[0, 1, 2, 3, 4].map((i) => (
                                  <button key={i} type="button" className={`${classes.tab} ${confTab === i ? classes.tabOn : (values[`confirmationMessage${i + 1}`] || "").trim() ? classes.tabFilled : ""}`} onClick={() => setConfTab(i)} aria-pressed={confTab === i}>{i + 1}</button>
                                ))}
                              </div>
                            </div>
                            <Editor name={`confirmationMessage${confTab + 1}`} values={values} setFieldValue={setFieldValue} placeholder={tc("messagePlaceholder")} minHeight={72} />
                          </>
                        )}

                        <div className={classes.preview}>
                          <div className={classes.previewLabel}>{tc("preview")}{previewContact?.name ? ` · ${previewContact.name}` : ""}</div>
                          {previewText ? (
                            <div className={classes.bubble}>
                              {(campaign.mediaPath || attachment) && <div className={classes.bubbleImg} />}
                              <div className={classes.bubbleText}>{previewText}<small>{values.scheduledAt ? moment(values.scheduledAt).format("HH:mm") : ""} ✓✓</small></div>
                            </div>
                          ) : <div className={classes.cardSub}>{tc("previewEmpty")}</div>}
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div className={classes.footer}>
                  {contactsForEstimate ? <div className={classes.estimate}><ScheduleIcon />{estimateLabel(contactsForEstimate, { ...settings, dailyLimit: limiteDiarioDoCanal(settings, canal) })}</div> : <span />}
                  <div className={classes.footBtns}>
                    {campaign.status === "CANCELADA" && <Button className={m.cancelButton} onClick={restartCampaign}>{tc("restart")}</Button>}
                    {campaign.status === "EM_ANDAMENTO" && <Button className={m.cancelButton} onClick={cancelCampaign}>{tc("pause")}</Button>}
                    {campaign.status === "PROGRAMADA" && campaignId && <Button className={m.cancelButton} onClick={cancelCampaign}>{tc("cancelScheduled")}</Button>}
                    <Button className={m.cancelButton} onClick={handleClose} disabled={isSubmitting}>{editable ? tc("cancel") : tc("close")}</Button>
                    {!ehSms && editable && <Button className={m.cancelButton} startIcon={<SendIcon />} disabled={!whatsappId || (!ehOficial && !filled.some(Boolean)) || (ehOficial && !quickMessageId)} onClick={() => setTestOpen(true)}>{tc("testMyNumber")}</Button>}
                    {(editable || campaign.status === "CANCELADA") && (
                      <Button type="submit" className={m.submitButton} disabled={isSubmitting} startIcon={isSubmitting ? <CircularProgress size={14} /> : <RocketIcon />}>
                        {campaignId ? tc("saveChanges") : tc("saveSchedule")}
                      </Button>
                    )}
                  </div>
                </div>

                <Popover open={Boolean(emojiAnchor)} anchorEl={emojiAnchor} onClose={() => setEmojiAnchor(null)} anchorOrigin={{ vertical: "top", horizontal: "left" }} transformOrigin={{ vertical: "bottom", horizontal: "left" }}>
                  <Picker perLine={9} showPreview={false} showSkinTones={false} onSelect={(emoji) => { if (emojiField) insertAtCursor(emojiField, emoji.native, values, setFieldValue); setEmojiAnchor(null); }} />
                </Popover>

                <Dialog open={testOpen} onClose={() => setTestOpen(false)} maxWidth={false} PaperProps={{ style: { width: 420, maxWidth: "calc(100vw - 32px)" } }}>
                  <div className={m.form}>
                    <div className={m.header}><div style={{ flex: 1 }}><div className={m.title}>{tc("testMyNumber")}</div><div className={m.subtitle}>{tc("testNoNumber")}</div></div><IconButton className={m.closeButton} onClick={() => setTestOpen(false)} aria-label={tc("close")}><CloseIcon /></IconButton></div>
                    <div style={{ padding: "16px 24px" }}>
                      <label className={classes.label}>{tc("testPrompt")}</label>
                      <div className={classes.field}><input value={testNumber} onChange={(e) => setTestNumber(e.target.value)} placeholder="5531999999999" inputMode="tel" aria-label={tc("testPrompt")} /></div>
                    </div>
                    <div className={m.footer}><div className={m.footerActions}>
                      <Button className={m.cancelButton} onClick={() => setTestOpen(false)}>{tc("cancel")}</Button>
                      <Button className={m.submitButton} disabled={testing || !testNumber} onClick={() => sendTest(values)} startIcon={testing ? <CircularProgress size={14} /> : <SendIcon />}>{tc("testMyNumber")}</Button>
                    </div></div>
                  </div>
                </Dialog>
              </Form>
            );
          }}
        </Formik>
      </Dialog>
    </div>
  );
};

export default CampaignModal;
